/**
 * Contenedor del panel de Notas de un aseo.
 *
 *  - ESCRITORIO / pantallas anchas: el `Modal` de siempre (mismo ancho, título
 *    y botón «Cerrar»).
 *  - CELULAR (<= 576 px): `Drawer` a PANTALLA COMPLETA con barra de acciones fija
 *    abajo («Cerrar» de 48 px). El modal de 90 % de ancho dejaba ~250 px útiles
 *    y los botones de subida quedaban apretados.
 *
 * Cerrar el panel NO cancela las subidas en curso (viven en `colaSubidaStore`);
 * se avisa para que la aseadora no bloquee el celular creyendo que terminó.
 */

import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import { Button, Drawer, Modal } from "antd";
import { getMsg } from "../../../utils/antdStatic";
import { colaNotas } from "./colaSubidaStore";
import { useEsMovil } from "./useMediaQuery";
import "../../../Css/notas-movil.css";

export default function NotasContenedor({
  open,
  aseoId,
  title,
  width = 680,
  onClose,
  children,
}: {
  open: boolean;
  /** Para saber si este aseo tiene subidas en curso. */
  aseoId: number | null | undefined;
  title: ReactNode;
  /** Ancho del modal en escritorio. */
  width?: number;
  onClose: () => void;
  children: ReactNode;
}) {
  const movil = useEsMovil();
  // Re-renderiza cuando cambia la cola para refrescar el aviso del pie.
  useSyncExternalStore(colaNotas.subscribe, colaNotas.getItems, colaNotas.getItems);
  const subiendo = aseoId != null && colaNotas.haySubidasActivas(aseoId);

  // El botón flotante del menú (hamburguesa) queda por ENCIMA del Drawer y tapaba
  // su X de cierre. Mientras el panel de celular está abierto se marca <body> y
  // el CSS (notas-movil.css) esconde ese botón; el panel trae su propio «Cerrar».
  useEffect(() => {
    if (!movil || !open) return undefined;
    document.body.classList.add("nm-panel-abierto");
    return () => document.body.classList.remove("nm-panel-abierto");
  }, [movil, open]);

  const cerrar = () => {
    if (subiendo) {
      getMsg().warning(
        "Las subidas siguen en curso. No cierres ni bloquees el celular hasta que terminen.",
        5,
      );
    }
    onClose();
  };

  if (movil) {
    return (
      <Drawer
        open={open}
        onClose={cerrar}
        placement="bottom"
        height="100%"
        title={title}
        destroyOnClose
        className="nm-drawer"
        data-testid="nm-panel-notas"
        footer={
          <div>
            {subiendo && (
              <div className="nm-pie-subiendo" role="status">
                Subiendo archivos… no cierres ni bloquees el celular.
              </div>
            )}
            <Button
              size="large"
              block
              style={{ minHeight: 48 }}
              onClick={cerrar}
              data-testid="nm-cerrar"
            >
              Cerrar
            </Button>
          </div>
        }
      >
        {children}
      </Drawer>
    );
  }

  return (
    <Modal
      open={open}
      title={title}
      onCancel={cerrar}
      footer={<Button onClick={cerrar}>Cerrar</Button>}
      width={width}
      destroyOnClose
    >
      {children}
    </Modal>
  );
}
